/** @jsx jsx */
import { useEffect, useState } from "react";
import BaseInput from "../Input";
import StepContainer from "../StepContainer";
import FormHelperText from "@material-ui/core/FormHelperText";
import { Flex, Box } from "rebass";

import { jsx, css } from "@emotion/core";
import InputMask from "react-input-mask";
import { useMachine } from "@xstate/react";
import { getStep4 } from "./machine";
import Button from "../Button";
import { State } from "xstate";

import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormLabel from "@material-ui/core/FormLabel";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const errors = {
  description: { empty: "Detailed equipment description is a required field" },
  locationDifferent: { empty: "Is Location Different is a required field" },
  streetAddress: { empty: "Street Address is a required field" },
  state: { empty: "State is a required field" },
  city: { empty: "City is a required field" },
  postalCode: { empty: "Zip / Postal Code is a required field" },
  hasLien: { empty: "Has Lien is a required field" },
  lienHolderName: { empty: "Lien holder name is a required field" },
  requiresDismantling: { empty: "Requires dismantling is a required field" },
  dismantleDays: {
    empty: "Dismantle days is a required field",
    invalid: "Please enter a number",
  },
  willHelpDismantle: { empty: "Will help dismantle is a required field" },
  dismantleHelpDescription: {
    empty: "Dismantle description is a required field",
  },
  providesDisconnect: { empty: "Will provide disconnect is a required field" },
  requiresCrane: { empty: "Requires crane is a required field" },
  numberOfTrucks: {
    empty: "Number of trucks is a required field",
    invalid: "Please enter a number",
  },
  requiresLoading: { empty: "Requires loading is a required field" },
  willLoadEquipment: { empty: "Will load equipment is a required field" },
  hasLoadingEquipment: { empty: "Has Loading equipment is a required field" },
  loadingEquipmentDescription: {
    empty: "Loading equipment description is a required field.",
  },
};

function getErrorMessage(
  item: string,
  current: State<any, any>
): string | undefined {
  const keys = errors[item] ? Object.keys(errors[item]) : [];
  let message = undefined;

  keys.some((key) => {
    if (current.matches(`ready.${item}.error.${key}`)) {
      message = errors[item][key];
    }
  });
  return message;
}

const Input = ({ current, ...props }) => (
  <BaseInput
    {...props}
    helperText={getErrorMessage(props.name, current)}
    css={css`
      margin-bottom: 20px;
    `}
  />
);

const RadioButtonGroup = ({
  keyName,
  value,
  onChange,
  label,
  current,
  children,
  required,
}: {
  keyName: string;
  value: any;
  onChange: () => void;
  label: string;
  current: any;
  children: any;
  required?: boolean;
}) => {
  return (
    <Box my="3">
      <FormLabel component="legend" required>
        {label}
      </FormLabel>
      <RadioGroup
        aria-label={label}
        name={keyName}
        value={value}
        onChange={onChange}
      >
        {children}
      </RadioGroup>
      {current.matches(`ready.${keyName}.error`) && (
        <FormHelperText error>
          {getErrorMessage(keyName, current as any)}
        </FormHelperText>
      )}
    </Box>
  );
};
export default function Step4({
  description,
  locationDifferent,
  streetAddress,
  streetAddress2,
  state,
  city,
  postalCode,
  hasLien,
  lienHolderName,
  lienHolderContact,
  requiresDismantling,
  dismantleDays,
  willHelpDismantle,
  dismantleHelpDescription,
  providesDisconnect,
  requiresCrane,
  numberOfTrucks,
  requiresLoading,
  willLoadEquipment,
  hasLoadingEquipment,
  onBack,
  onSubmit,
}: {
  description?: string;
  locationDifferent?: boolean;
  streetAddress?: string;
  streetAddress2?: string;
  state?: string;
  city?: string;
  postalCode?: number;
  hasLien?: boolean;
  lienHolderName?: string;
  lienHolderContact?: string;
  requiresDismantling?: boolean;
  dismantleDays?: number;
  willHelpDismantle?: boolean;
  dismantleHelpDescription?: string;
  providesDisconnect?: boolean;
  requiresCrane?: boolean;
  numberOfTrucks?: number;
  requiresLoading?: boolean;
  willLoadEquipment?: boolean;
  hasLoadingEquipment?: boolean;
  onBack: (values: any) => void;
  onSubmit: (values: any) => void;
}) {
  const [current, send] = useMachine(
    getStep4({
      description,
      locationDifferent,
      streetAddress,
      streetAddress2,
      state,
      city,
      postalCode,
      hasLien,
      lienHolderName,
      lienHolderContact,
      requiresDismantling,
      dismantleDays,
      willHelpDismantle,
      dismantleHelpDescription,
      providesDisconnect,
      requiresCrane,
      numberOfTrucks,
      requiresLoading,
      willLoadEquipment,
      hasLoadingEquipment,
    })
  );
  const finished = current.matches("finished");

  useEffect(() => {
    finished && onSubmit(current.context);
  }, [finished, current.context, onSubmit]);

  useEffect(() => {
    send("INIT");
  }, []);

  const handleChange: any = (type: string, property: string) => (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    send({
      type,
      [property]: e.currentTarget ? e.currentTarget.value : "",
    });
  };

  const handleDescriptionChange = handleChange(
    "INPUT_DESCRIPTION",
    "description"
  );

  const handleLocationDifferentChange = handleChange(
    "INPUT_LOCATION_DIFFERENT",
    "locationDifferent"
  );

  const handleStreetAddressChange = handleChange(
    "INPUT_STREET_ADDRESS",
    "streetAddress"
  );
  const handleStreetAddress2Change = handleChange(
    "INPUT_STREET_ADDRESS2",
    "streetAddress2"
  );
  const handleCityChange = handleChange("INPUT_CITY", "city");
  const handleStateChange = handleChange("INPUT_STATE", "state");
  const handlePostalCodeChange = handleChange(
    "INPUT_POSTAL_CODE",
    "postalCode"
  );

  const handleHasLien = handleChange("INPUT_HAS_LIEN", "hasLien");
  const handleLienholderNameChange = handleChange(
    "INPUT_LIEN_HOLDER_NAME",
    "lienHolderName"
  );

  const handleLienholderContactChange = handleChange(
    "INPUT_LIEN_HOLDER_CONTACT",
    "lienHolderContact"
  );

  const handleLienPayoutAmountChange = handleChange(
    "INPUT_LIEN_PAYOUT_AMOUNT",
    "lienPayoutAmount"
  );

  const handleRequiresDismantlingChange = handleChange(
    "INPUT_REQUIRES_DISMANTLING",
    "requiresDismantling"
  );

  const handleDismantleDaysChange = handleChange(
    "INPUT_DISMANTLE_DAYS",
    "dismantleDays"
  );

  const handleWillHelpDismantleChange = handleChange(
    "INPUT_WILL_HELP_DISMANTLE",
    "willHelpDismantle"
  );

  const handleDismantleHelpDescription = handleChange(
    "INPUT_DISMANTLE_HELP_DESCRIPTION",
    "dismantleHelpDescription"
  );

  const handleProvidesDisconnectChange = handleChange(
    "INPUT_PROVIDES_DISCONNECT",
    "providesDisconnect"
  );

  const handleRequiresCraneChange = handleChange(
    "INPUT_REQUIRES_CRANE",
    "requiresCrane"
  );

  const handleNumberOfTrucksChange = handleChange(
    "INPUT_NUMBER_OF_TRUCKS",
    "numberOfTrucks"
  );

  const handleRequiresLoadingChange = handleChange(
    "INPUT_REQUIRES_LOADING",
    "requiresLoading"
  );

  const handleWillLoadEquipmentChange = handleChange(
    "INPUT_WILL_LOAD_EQUIPMENT",
    "willLoadEquipment"
  );

  const handleHasLoadingEquipmentChange = handleChange(
    "INPUT_HAS_LOADING_EQUIPMENT",
    "hasLoadingEquipment"
  );

  const handleLoadingEquipmentDescriptionChange = handleChange(
    "INPUT_LOADING_EQUIPMENT_DESCRIPTION",
    "loadingEquipmentDescription"
  );

  return (
    <StepContainer title="Step 4 / 5: Tell Us About It">
      <Input
        required
        multiline
        rows="4"
        variant="filled"
        error={current.matches("ready.description.error")}
        label="Detailed equipment description"
        placeholder="As much detail as you can give us. The more descriptive you are, the faster your equipment sells."
        name="description"
        value={current.context.description}
        current={current}
        onChange={handleDescriptionChange}
      />
      <Box my="3">
        <FormLabel component="legend" required>
          Is this equipment at a different address than the company address?
        </FormLabel>
        <RadioGroup
          aria-label="Is equipment location different than company?"
          name="locationDifferent"
          value={current.context.locationDifferent}
          onChange={handleLocationDifferentChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {current.matches("ready.locationDifferent.error") && (
          <FormHelperText error>
            {getErrorMessage("locationDifferent", current as any)}
          </FormHelperText>
        )}
      </Box>
      {current.matches("ready.locationDifferent.noError.yes") && (
        <Flex flexDirection="column">
          <Input
            required
            error={current.matches("ready.streetAddress.error")}
            value={current.context.streetAddress}
            label="Street Address"
            name="streetAddress"
            current={current}
            onChange={handleStreetAddressChange}
          />
          <Input
            error={current.matches("ready.streetAddress2.error")}
            value={current.context.streetAddress2}
            label="Street Address Line 2"
            name="streetAddress2"
            current={current}
            onChange={handleStreetAddress2Change}
          />
          <Input
            required
            error={current.matches("ready.city.error")}
            value={current.context.city}
            label="City"
            name="city"
            current={current}
            onChange={handleCityChange}
          />
          <Input
            required
            error={current.matches("ready.state.error")}
            value={current.context.state}
            label="State"
            name="state"
            current={current}
            onChange={handleStateChange}
          />
          <Input
            required
            error={current.matches("ready.postalCode.error")}
            value={current.context.postalCode}
            label="Zip"
            name="postalCode"
            onChange={handlePostalCodeChange}
            current={current}
          />
        </Flex>
      )}
      <Box my="3">
        <FormLabel component="legend" required>
          Is there a lien on the equipment?
        </FormLabel>
        <RadioGroup
          aria-label="Is there a lien on the equipment?"
          name="hasLien"
          value={current.context.hasLien}
          onChange={handleHasLien}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {current.matches("ready.hasLien.error") && (
          <FormHelperText error>
            {getErrorMessage("hasLien", current as any)}
          </FormHelperText>
        )}
      </Box>
      {current.matches("ready.hasLien.noError.yes") && (
        <Flex flexDirection="column">
          <Input
            required
            error={current.matches("ready.lienHolderName.error")}
            value={current.context.lienHolderName}
            label="Lienholder Name"
            name="lienHolderName"
            current={current}
            onChange={handleLienholderNameChange}
          />

          <Input
            required
            error={current.matches("ready.lienHolderContact.error")}
            value={current.context.lienHolderContact}
            label="Lienholder Contact Information"
            name="lienHolderContact"
            current={current}
            onChange={handleLienholderContactChange}
          />

          <Input
            required
            error={false}
            value={current.context.lienPayoutAmount}
            label="Lien Payout Amount"
            name="lienPayoutAmount"
            current={current}
            onChange={handleLienPayoutAmountChange}
          />
        </Flex>
      )}
      <Box my="3">
        <FormLabel component="legend" required>
          Does this equipment require dismantling?
        </FormLabel>
        <RadioGroup
          aria-label="Does this equipment require dismantling?"
          name="requiresDismantling"
          value={current.context.requiresDismantling}
          onChange={handleRequiresDismantlingChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {current.matches("ready.requiresDismantling.error") && (
          <FormHelperText error>
            {getErrorMessage("requiresDismantling", current as any)}
          </FormHelperText>
        )}
      </Box>
      {current.matches("ready.requiresDismantling.noError.yes") && (
        <Flex flexDirection="column">
          <Input
            required
            error={current.matches("ready.dismantleDays.error")}
            value={current.context.dismantleDays}
            label="How many days are you willing to give a buyer to dismantle and ship the equipment?"
            name="dismantleDays"
            current={current}
            onChange={handleDismantleDaysChange}
          />

          <Box my="3">
            <FormLabel component="legend" required>
              Are you willing to help with the dismantling process?
            </FormLabel>
            <RadioGroup
              aria-label="Are you willing to help with the dismantling process?"
              name="willHelpDismantle"
              value={current.context.willHelpDismantle}
              onChange={handleWillHelpDismantleChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
            {current.matches("ready.willHelpDismantle.error") && (
              <FormHelperText error>
                {getErrorMessage("willHelpDismantle", current as any)}
              </FormHelperText>
            )}
          </Box>
          {current.matches("ready.willHelpDismantle.noError.yes") && (
            <Input
              required
              error={current.matches("ready.dismantleHelpDescription.error")}
              value={current.context.dismantleHelpDescription}
              label="To what extent are you willing to help?"
              name="dismantleHelpDescription"
              current={current}
              onChange={handleDismantleHelpDescription}
            />
          )}
          <Box my="3">
            <FormLabel component="legend" required>
              Will dismantling require crane service?
            </FormLabel>
            <RadioGroup
              aria-label="Will dismantling require crane service?"
              name="requiresCrane"
              value={current.context.requiresCrane}
              onChange={handleRequiresCraneChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
            {current.matches("ready.requiresCrane.error") && (
              <FormHelperText error>
                {getErrorMessage("requiresCrane", current as any)}
              </FormHelperText>
            )}
          </Box>
        </Flex>
      )}
      <Box my="3">
        <FormLabel component="legend" required>
          Will you provide the electrical disconnect if required?
        </FormLabel>
        <RadioGroup
          aria-label="Will you provide the electrical disconnect if required?"
          name="providesDisconnect"
          value={current.context.providesDisconnect}
          onChange={handleProvidesDisconnectChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {current.matches("ready.providesDisconnect.error") && (
          <FormHelperText error>
            {getErrorMessage("providesDisconnect", current as any)}
          </FormHelperText>
        )}
      </Box>

      <Box my="3">
        <FormLabel component="legend" required>
          Does this equipment require loading?
        </FormLabel>
        <RadioGroup
          aria-label="Does this equipment require loading?"
          name="requiresLoading"
          value={current.context.requiresLoading}
          onChange={handleRequiresLoadingChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {current.matches("ready.requiresLoading.error") && (
          <FormHelperText error>
            {getErrorMessage("requiresLoading", current as any)}
          </FormHelperText>
        )}
      </Box>
      {current.matches("ready.requiresLoading.noError.yes") && (
        <Input
          required
          error={current.matches("ready.numberOfTrucks.error")}
          value={current.context.numberOfTrucks}
          label="Please provide an estimated number of trucks to ship the equipment"
          name="numberOfTrucks"
          current={current}
          onChange={handleNumberOfTrucksChange}
        />
      )}
      {current.matches("ready.requiresLoading.noError.yes") && (
        <RadioButtonGroup
          required
          keyName="willLoadEquipment"
          label="Will you load the equipment on to the buyer's trucks?"
          current={current}
          value={current.context.willLoadEquipment}
          onChange={handleWillLoadEquipmentChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioButtonGroup>
      )}

      {current.matches("ready.willLoadEquipment.noError.no") && (
        <RadioButtonGroup
          required
          keyName="hasLoadingEquipment"
          label="Is there anything onsite a buyer may utilize to load the equipment (loaders, dock, crane, etc.)?"
          current={current}
          value={current.context.hasLoadingEquipment}
          onChange={handleHasLoadingEquipmentChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioButtonGroup>
      )}

      {current.matches("ready.willLoadEquipment.noError.no") &&
        current.matches("ready.hasLoadingEquipment.noError.yes") && (
          <Input
            required
            error={current.matches("ready.loadingEquipmentDescription.error")}
            value={current.context.loadingEquipmentDescription}
            label="What loading equipment will be available?"
            name="loadingEquipmentDescription"
            current={current}
            onChange={handleLoadingEquipmentDescriptionChange}
          />
        )}
      <Flex justifyContent="space-between" mt="4">
        <Button onClick={() => onBack(current.context)} width={200}>
          Back
        </Button>

        <Button
          onClick={() => {
            send("SUBMIT");
          }}
          width={200}
          color="primary"
        >
          Continue
        </Button>
      </Flex>
    </StepContainer>
  );
}
